import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "../lib/api";

export const ImageLightbox = ({ images = [], index = 0, open, onClose, onIndex }) => {
  const total = images.length;

  const go = useCallback(
    (dir) => {
      if (!total) return;
      onIndex((index + dir + total) % total);
    },
    [index, total, onIndex]
  );

  useEffect(() => {
    if (!open) return;
    const lenis = window.__stoofLenis;
    lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, go, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[120] bg-black flex flex-col"
          data-testid="image-lightbox"
          onClick={onClose}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-4 text-white/90 relative z-10" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm font-semibold tracking-wide" data-testid="lightbox-counter">
              {index + 1} / {total}
            </span>
            <button
              onClick={onClose}
              data-testid="lightbox-close"
              aria-label="Sluiten"
              className="h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 active:scale-95 transition"
            >
              <X size={22} />
            </button>
          </div>

          {/* Image (swipeable) */}
          <div className="flex-1 relative overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.img
                key={index}
                src={fileUrl(images[index])}
                alt=""
                data-testid="lightbox-image"
                className="absolute inset-0 m-auto max-h-full max-w-full object-contain select-none"
                draggable={false}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.6}
                onDragEnd={(e, info) => {
                  if (info.offset.x < -70) go(1);
                  else if (info.offset.x > 70) go(-1);
                }}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0.2 }}
                transition={{ duration: 0.25 }}
              />
            </AnimatePresence>
          </div>

          {/* Nav arrows (hidden on very small, swipe still works) */}
          {total > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); go(-1); }}
                data-testid="lightbox-prev"
                aria-label="Vorige foto"
                className="hidden sm:grid place-items-center absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white active:scale-95 transition"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); go(1); }}
                data-testid="lightbox-next"
                aria-label="Volgende foto"
                className="hidden sm:grid place-items-center absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white active:scale-95 transition"
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}

          {/* Thumbnail strip */}
          {total > 1 && (
            <div className="px-4 py-4 flex gap-2 overflow-x-auto relative z-10" onClick={(e) => e.stopPropagation()}>
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => onIndex(i)}
                  className={`shrink-0 h-14 w-20 overflow-hidden rounded border-2 transition ${i === index ? "border-white" : "border-transparent opacity-50"}`}
                >
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
